import { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, X, Loader2 } from "lucide-react";
import {
  PagReviewUser,
  fetchPagUsersList,
  fetchPagReviewConfig,
  savePagReviewConfig,
} from "../../../../api/frameworksApi";
import { Modal, FormSelect, ModalFooterButtons } from "../../../components/Modal";
import { Tip } from "../../../components/ui/tooltip";
import { showToast } from "../../../components/ui/custom-toast";

/* ─── Types ───────────────────────────────────────────────────────────────── */

/**
 * Which trailing segment a reviewer pill renders. The badge is driven by this
 * explicit role — never by the reviewer's position in the array — so a pill
 * keeps its P/S marker regardless of ordering or later additions.
 */
export type ReviewerRole = "PRIMARY" | "SECONDARY" | "MANUAL";

export interface AccessReviewer {
  id: string;
  name: string;
  role: ReviewerRole;
}

export interface AppAccessReview {
  reviewers: AccessReviewer[];
  /** Display string the API round-trips verbatim ("Half-Yearly"), "" if unset. */
  reviewCycle: string;
}

/** Fixed display order — intentionally not sorted alphabetically. The value is
 *  the same string the API sends and expects back. */
export const REVIEW_CYCLES: { value: string; label: string }[] = [
  { value: "Monthly", label: "Monthly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Half-Yearly", label: "Half-Yearly" },
  { value: "Yearly", label: "Yearly" },
];

/** Primary owner first, secondary second, everyone else in insertion order. */
const ROLE_WEIGHT: Record<ReviewerRole, number> = {
  PRIMARY: 0,
  SECONDARY: 1,
  MANUAL: 2,
};

const orderReviewers = (list: AccessReviewer[]) =>
  [...list].sort((a, b) => ROLE_WEIGHT[a.role] - ROLE_WEIGHT[b.role]);

const ROLE_BADGE: Record<ReviewerRole, { letter: string; title: string } | null> = {
  PRIMARY: { letter: "P", title: "Primary Owner" },
  SECONDARY: { letter: "S", title: "Secondary Owner" },
  MANUAL: null,
};

/* ─── Reviewer pill ───────────────────────────────────────────────────────── */

/**
 * Two-segment pill fused into one shape: the name segment on the left and an
 * optional trailing segment (P / S badge, or a remove control inside the edit
 * form). With no trailing segment the name segment rounds on all four corners.
 */
function ReviewerPill({
  reviewer,
  onRemove,
}: {
  reviewer: AccessReviewer;
  onRemove?: (reviewer: AccessReviewer) => void;
}) {
  const badge = ROLE_BADGE[reviewer.role];
  // The remove control only appears for manually added reviewers — the primary
  // and secondary owners are fixed and cannot be taken off the review.
  const removable = Boolean(onRemove) && reviewer.role === "MANUAL";
  const hasTrailing = Boolean(badge) || removable;

  return (
    <span className="inline-flex items-stretch max-w-full">
      <span
        className={`inline-flex items-center border-2 border-indigo-600 dark:border-indigo-500 px-1.5 py-1 text-xs leading-none text-foreground break-words ${
          hasTrailing ? "rounded-l-md" : "rounded-md"
        }`}
      >
        {reviewer.name}
      </span>

      {badge && (
        <Tip label={badge.title} side="top">
          <span className="inline-flex items-center rounded-r-md border-2 border-indigo-600 dark:border-indigo-500 bg-indigo-600 dark:bg-indigo-500 pl-0.5 pr-1.5 py-1 text-xs font-semibold leading-none text-white">
            {badge.letter}
          </span>
        </Tip>
      )}

      {!badge && removable && (
        <Tip label={`Remove ${reviewer.name}`} side="top">
          <button
            type="button"
            onClick={() => onRemove?.(reviewer)}
            aria-label={`Remove ${reviewer.name}`}
            className="inline-flex items-center rounded-r-md border-2 border-l border-indigo-600 dark:border-indigo-500 bg-card px-1.5 py-1 leading-none text-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
          >
            <X className="size-3" />
          </button>
        </Tip>
      )}
    </span>
  );
}

function CenteredSpinner({ className = "py-8" }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <Loader2 className="size-5 animate-spin text-indigo-600 dark:text-indigo-400" />
    </div>
  );
}

/* ─── Summary card (read-only) ────────────────────────────────────────────── */

export function AppAccessReviewCard({
  review,
  loading,
  onEdit,
  canEdit = true,
}: {
  review: AppAccessReview;
  loading?: boolean;
  onEdit?: () => void;
  canEdit?: boolean;
}) {
  const reviewers = useMemo(
    () => orderReviewers(review.reviewers),
    [review.reviewers],
  );

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="text-base font-bold text-indigo-700 dark:text-indigo-400">
          Application User Access Review
        </h4>
        {canEdit && onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
          >
            Edit
            <Pencil className="size-3.5" />
          </button>
        )}
      </div>

      {loading ? (
        <CenteredSpinner className="py-6" />
      ) : (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-foreground">
              Reviewers :
            </span>
            {reviewers.length === 0 ? (
              <span className="text-sm d">Not Available</span>
            ) : (
              reviewers.map((r) => <ReviewerPill key={r.id} reviewer={r} />)
            )}
          </div>

          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-sm font-semibold text-foreground">
              Review Frequency :
            </span>
            <span className="text-sm text-foreground">
              {review.reviewCycle || <span className="d">Not Available</span>}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Reviewer typeahead ──────────────────────────────────────────────────── */

function ReviewerTypeahead({
  users,
  loading,
  selected,
  onSelect,
  query,
  onQueryChange,
}: {
  users: PagReviewUser[];
  loading?: boolean;
  selected: PagReviewUser | null;
  onSelect: (user: PagReviewUser | null) => void;
  query: string;
  onQueryChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Close the suggestion list on any click outside the combobox.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? users.filter(
          (u) =>
            u.displayName?.toLowerCase().includes(q) ||
            u.email?.toLowerCase().includes(q),
        )
      : users;
    return list.slice(0, 50);
  }, [users, query]);

  return (
    <div ref={boxRef} className="relative flex-1 min-w-0">
      <input
        type="text"
        value={query}
        maxLength={120}
        placeholder="Enter Reviewer"
        aria-label="Enter Reviewer"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onQueryChange(e.target.value);
          // Any edit invalidates the previous pick — Add stays disabled until
          // a user is chosen from the list again.
          if (selected) onSelect(null);
          setOpen(true);
        }}
        className="h-10 w-full rounded-lg border border-[#e2e8f0] bg-[#f5f7fb] px-3 text-sm text-[#0f172a] outline-none transition-all placeholder:text-[#64748b] focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 dark:border-white/[0.07] dark:bg-white/[0.04] dark:text-white dark:placeholder:text-white/25"
      />

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-10 max-h-52 overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
          {loading ? (
            <CenteredSpinner className="py-4" />
          ) : matches.length === 0 ? (
            <p className="px-3 py-2.5 text-sm d">No users found</p>
          ) : (
            matches.map((u) => (
              <button
                key={u.userId}
                type="button"
                onClick={() => {
                  onSelect(u);
                  onQueryChange(u.displayName || u.email);
                  setOpen(false);
                }}
                className="block w-full px-3 py-2 text-left transition-colors hover:bg-accent"
              >
                <span className="block text-sm text-foreground">
                  {u.displayName || u.email}
                </span>
                {u.displayName && u.email && (
                  <span className="block text-xs d">{u.email}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Modal (summary card + edit form) ────────────────────────────────────── */

interface AppAccessReviewModalProps {
  open: boolean;
  onClose: () => void;
  /** CMDB application id — the `cmdbId` the review config is keyed by. */
  applicationId: string;
  /** Application the review belongs to — shown as the modal description. */
  applicationName: string;
  /** Hides the Edit button for users without update rights. */
  canEdit?: boolean;
}

const EMPTY_REVIEW: AppAccessReview = { reviewers: [], reviewCycle: "" };

export function AppAccessReviewModal({
  open,
  onClose,
  applicationId,
  applicationName,
  canEdit = true,
}: AppAccessReviewModalProps) {
  const [mode, setMode] = useState<"view" | "edit">("view");

  // Saved review as the API last reported it.
  const [review, setReview] = useState<AppAccessReview>(EMPTY_REVIEW);
  const [loading, setLoading] = useState(false);

  // Draft state — nothing here reaches `review` until Save.
  const [draftReviewers, setDraftReviewers] = useState<AccessReviewer[]>([]);
  const [draftCycle, setDraftCycle] = useState("");
  const [saving, setSaving] = useState(false);

  // Reviewer picker
  const [users, setUsers] = useState<PagReviewUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  // A ref, not state: the fetch guard must not be an effect dependency, or the
  // effect re-runs the moment the request starts.
  const usersRequestedRef = useRef(false);
  const [query, setQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<PagReviewUser | null>(null);

  // Reset to the read-only card and load the saved config every time the popup
  // is opened for a row, so nothing leaks from the previously viewed row.
  useEffect(() => {
    if (!open || !applicationId) return;
    setMode("view");
    setDraftReviewers([]);
    setDraftCycle("");
    setSaving(false);
    setQuery("");
    setSelectedUser(null);
    loadReview();
    // loadReview is stable for a given applicationId — see its definition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, applicationId]);

  // Platform users are only needed by the picker — fetch them lazily the first
  // time the edit form is opened, never on the read-only card.
  useEffect(() => {
    if (mode !== "edit" || usersRequestedRef.current) return;
    usersRequestedRef.current = true;
    setUsersLoading(true);
    fetchPagUsersList()
      // Deactivated accounts cannot own a review, so they never reach the list.
      .then((list) => setUsers((list ?? []).filter((u) => u.active !== false)))
      .catch(() => {
        // Let a later visit to the form retry the fetch.
        usersRequestedRef.current = false;
        showToast("error", "Could not load the user list");
      })
      .finally(() => setUsersLoading(false));
  }, [mode]);

  /**
   * The config endpoint answers 404/500 for an application that has never been
   * configured, so a failed read is treated as "nothing saved yet" and the card
   * simply shows Not Available — it must not block the popup.
   */
  const loadReview = async () => {
    setLoading(true);
    try {
      const cfg = await fetchPagReviewConfig(applicationId);
      setReview({
        reviewCycle: cfg?.reviewCycle ?? "",
        reviewers: (cfg?.reviewers ?? []).map((r) => ({
          id: r.userId,
          name: r.displayName || r.email,
          // The endpoint carries no owner role, so every reviewer renders as a
          // plain removable pill. Map PRIMARY/SECONDARY here once it does.
          role: "MANUAL" as ReviewerRole,
        })),
      });
    } catch {
      setReview(EMPTY_REVIEW);
    } finally {
      setLoading(false);
    }
  };

  const openEdit = () => {
    setDraftReviewers(orderReviewers(review.reviewers));
    setDraftCycle(review.reviewCycle);
    setQuery("");
    setSelectedUser(null);
    setMode("edit");
  };

  const backToView = () => {
    setQuery("");
    setSelectedUser(null);
    setMode("view");
  };

  const addReviewer = () => {
    if (!selectedUser) return;
    const name = selectedUser.displayName || selectedUser.email;
    const exists = draftReviewers.some(
      (r) =>
        r.id === selectedUser.userId ||
        r.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );
    if (exists) {
      showToast("warning", "Selected reviewer already exist");
      return;
    }
    setDraftReviewers((prev) => [
      ...prev,
      { id: selectedUser.userId, name, role: "MANUAL" },
    ]);
    setQuery("");
    setSelectedUser(null);
  };

  const removeReviewer = (reviewer: AccessReviewer) =>
    setDraftReviewers((prev) => prev.filter((r) => r.id !== reviewer.id));

  const handleSave = async () => {
    if (!draftCycle) {
      showToast("warning", "Please check all the fields are filled");
      return;
    }
    setSaving(true);
    try {
      await savePagReviewConfig({
        cmdbId: applicationId,
        reviewCycle: draftCycle,
        userIds: orderReviewers(draftReviewers).map((r) => r.id),
      });
      // Show what the server now holds (due dates are derived server-side).
      await loadReview();
      setMode("view");
      showToast("success", "User access review updated");
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Could not save the user access review",
      );
    } finally {
      setSaving(false);
    }
  };

  const isEdit = mode === "edit";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Update User Access Review" : "Application User Access Review"}
      description={applicationName}
      onBack={isEdit ? backToView : undefined}
      footer={
        isEdit ? (
          <ModalFooterButtons
            onCancel={backToView}
            onSubmit={handleSave}
            submitLabel="Save"
            submitDisabled={saving}
          />
        ) : (
          <ModalFooterButtons onCancel={onClose} cancelText="Close" />
        )
      }
    >
      {!isEdit && (
        <AppAccessReviewCard
          review={review}
          loading={loading}
          onEdit={openEdit}
          canEdit={canEdit}
        />
      )}

      {isEdit &&
        (saving ? (
          <CenteredSpinner className="py-16" />
        ) : (
          <div className="space-y-4">
            {/* Reviewers box */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Reviewers
              </label>
              <div className="h-[150px] w-full overflow-y-auto rounded-lg border border-border bg-muted/40 p-3">
                {draftReviewers.length === 0 ? (
                  <p className="text-sm d">No reviewers added yet</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {orderReviewers(draftReviewers).map((r) => (
                      <ReviewerPill
                        key={r.id}
                        reviewer={r}
                        onRemove={removeReviewer}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Add reviewer + review cycle */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex items-center gap-2">
                <ReviewerTypeahead
                  users={users}
                  loading={usersLoading}
                  selected={selectedUser}
                  onSelect={setSelectedUser}
                  query={query}
                  onQueryChange={setQuery}
                />
                <Tip
                  label={selectedUser ? "" : "Please Select Any User"}
                  side="top"
                >
                  <button
                    type="button"
                    onClick={addReviewer}
                    disabled={!selectedUser}
                    className="h-10 flex-shrink-0 rounded-lg bg-indigo-600 px-4 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-40"
                  >
                    Add
                  </button>
                </Tip>
              </div>

              <div className="flex items-center gap-3 sm:justify-end">
                <span className="whitespace-nowrap text-sm font-medium text-foreground">
                  Review Cycle :
                </span>
                <div className="min-w-0 flex-1 sm:max-w-[190px]">
                  <FormSelect
                    value={draftCycle}
                    onChange={setDraftCycle}
                    options={[
                      { value: "", label: "Select cycle" },
                      ...REVIEW_CYCLES,
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
    </Modal>
  );
}
